import type { CmtsHttpClient } from "../api/cmtsHttpClient.js";
import { CMTS_API } from "../api/cmtsApiPaths.js";
import { buildPermissionContext } from "./permissionBridge.js";
import type { CmtsUserContext } from "../view-models/complaintViewModels.js";
import { readString } from "../mappers/mapperUtils.js";
import { mapCollection } from "../repositories/repositoryCollection.js";
import { repositoryCall } from "../repositories/repositoryResult.js";
import { clearCmtsSession, getStoredRefreshToken, getStoredTenantCode, getStoredTenantId, mergeCmtsSession, writeCmtsSession } from "./authStore.js";
import type { LoginRequest, LoginResponse, QuickAccessUser } from "@services/cmtsLoginApi.js";
import { normalizeCmtsRoles } from "./normalizeCmtsRoles.js";

export type LoginPayload = LoginRequest;
export type AuthRepository = {
  login(payload: LoginPayload): Promise<unknown>;
  quickAccessUsers(tenantCode: string): Promise<readonly QuickAccessUser[]>;
  refresh(refreshToken?: string): Promise<unknown>;
  logout(tenantId?: string): Promise<void>;
  changePassword(payload: Record<string, unknown>): Promise<unknown>;
  requestOtp(payload: Record<string, unknown>): Promise<unknown>;
  verifyOtp(payload: Record<string, unknown>): Promise<unknown>;
  sessions(tenantId?: string): Promise<readonly unknown[]>;
  revokeSession(sessionId: string, tenantId?: string): Promise<unknown>;
  me(tenantId?: string): Promise<CmtsUserContext>;
  myPermissions(tenantId?: string): Promise<readonly string[]>;
  myOrganizationScope(tenantId?: string): Promise<readonly unknown[]>;
  myMenu(tenantId?: string): Promise<readonly { id: string; label: string; href?: string | undefined; permission?: string | undefined }[]>;
};

function resolveTenantId(explicit?: string): string | undefined { return explicit || getStoredTenantId(); }
function resolveTenantCode(explicit?: string): string | undefined { return explicit || getStoredTenantCode(); }
function readRecord(value: unknown): Record<string, unknown> { return typeof value === "object" && value !== null ? value as Record<string, unknown> : {}; }
function readNestedRecord(value: Record<string, unknown>, keys: readonly string[]): Record<string, unknown> {
  for (const key of keys) {
    const candidate = value[key];
    if (typeof candidate === "object" && candidate !== null) return candidate as Record<string, unknown>;
  }
  return {};
}
function readStringArrayFrom(value: unknown, keys: readonly string[] = []): readonly string[] {
  if (Array.isArray(value)) return Array.from(new Set(value.map((item) => {
    if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") return String(item).trim();
    return readString(readRecord(item), ["code", "Code", "permissionCode", "PermissionCode", "name", "Name", "roleCode", "RoleCode"], "");
  }).filter(Boolean))).sort();
  const record = readRecord(value);
  for (const key of keys) {
    const candidate = record[key];
    const normalized = readStringArrayFrom(candidate);
    if (normalized.length > 0) return normalized;
  }
  return [];
}

function readCollectionFrom(value: unknown, keys: readonly string[] = []): readonly unknown[] {
  if (Array.isArray(value)) return value;
  const record = readRecord(value);
  for (const key of keys) {
    const candidate = record[key];
    if (Array.isArray(candidate)) return candidate;
  }
  return [];
}

function flattenMenu(value: unknown): readonly unknown[] {
  const input = readCollectionFrom(value, ["items", "Items", "data", "Data", "menu", "Menu", "menuEntries", "MenuEntries"]);
  const output: unknown[] = [];
  const visit = (item: unknown) => {
    output.push(item);
    for (const child of readCollectionFrom(item, ["children", "Children"])) visit(child);
  };
  input.forEach(visit);
  return output;
}

export function createAuthRepository(client: CmtsHttpClient): AuthRepository {
  return {
    login: async (payload) => {
      const tenantCode = payload.tenantCode?.trim() || resolveTenantCode();
      if (!tenantCode) {
        throw new Error("Tenant Code is required for CMTS login.");
      }
      const result = await client.post<LoginResponse & Record<string, unknown>>(CMTS_API.auth.login, { tenantCode, loginId: payload.loginId, password: payload.password });
      const user = readNestedRecord(result, ["user", "User"]);
      const permissions = readStringArrayFrom(result.permissions ?? result.Permissions);
      const roles = normalizeCmtsRoles(readStringArrayFrom(user.roles ?? user.Roles));
      const tenantRecord = readNestedRecord(result, ["tenant", "Tenant"]);
      const tenantId = readString(tenantRecord, ["tenantId", "TenantId", "id", "Id"], readString(user, ["tenantId", "TenantId"], readString(result, ["tenantId", "TenantId"], "")));
      const tenantName = readString(tenantRecord, ["tenantName", "TenantName", "name", "Name"], readString(user, ["tenantName", "TenantName"], readString(result, ["tenantName", "TenantName"], "")));
      writeCmtsSession({
        accessToken: readString(result, ["accessToken", "AccessToken", "token", "jwt"], ""),
        refreshToken: readString(result, ["refreshToken", "RefreshToken"], ""),
        expiresAtUtc: readString(result, ["accessTokenExpiresAt", "AccessTokenExpiresAt", "expiresAtUtc"], ""),
        userId: readString(user, ["userId", "UserId", "id", "Id"], ""),
        tenantId,
        tenantCode,
        tenantName,
        organizationUnitId: readString(user, ["organizationUnitId", "OrganizationUnitId", "orgUnitId", "OrgUnitId"], readString(result, ["organizationUnitId", "OrganizationUnitId", "orgUnitId", "OrgUnitId"], "")),
        organizationUnitIds: readStringArrayFrom(user.organizationUnitIds ?? user.OrganizationUnitIds ?? user.orgScopes ?? user.organizationScopes ?? result.organizationUnitIds ?? result.OrganizationUnitIds, ["organizationUnitIds", "OrganizationUnitIds", "orgScopes", "organizationScopes"]),
        displayName: readString(user, ["displayName", "fullName", "FullName", "name"], "CMTS User"),
        email: readString(user, ["email", "Email"], ""),
        roles,
        permissions,
        permissionCodes: permissions,
      });
      return result;
    },
    quickAccessUsers: async (tenantCode) => mapCollection(await client.get<unknown>(CMTS_API.auth.quickAccessUsers, { query: { tenantCode } }), (item): QuickAccessUser => {
      const record = readRecord(item);
      return {
        userId: readString(record, ["userId", "UserId", "id", "Id"], ""),
        userCode: readString(record, ["userCode", "UserCode", "code", "Code"], ""),
        loginId: readString(record, ["loginId", "LoginId", "username", "email", "Email"], ""),
        displayName: readString(record, ["displayName", "DisplayName", "fullName", "FullName", "name"], "CMTS User"),
        email: readString(record, ["email", "Email"], ""),
        roles: normalizeCmtsRoles(readStringArrayFrom(record.roles ?? record.Roles, ["roles", "Roles"])),
        tenantCode: readString(record, ["tenantCode", "TenantCode"], tenantCode),
        orgScopes: readStringArrayFrom(record.orgScopes ?? record.organizationScopes ?? record.OrganizationScopes, ["orgScopes", "organizationScopes", "OrganizationScopes"]),
        isActive: record.isActive !== false && record.IsActive !== false,
        canLogin: record.canLogin !== false && record.CanLogin !== false,
        permissionSummary: readStringArrayFrom(record.permissionSummary ?? record.permissions ?? record.Permissions, ["permissionSummary", "permissions", "Permissions"]),
      };
    }),
    refresh: async (refreshToken) => {
      const result = await client.post<Record<string, unknown>>(CMTS_API.auth.refresh, { refreshToken: refreshToken || getStoredRefreshToken() });
      mergeCmtsSession({
        accessToken: readString(result, ["accessToken", "token", "jwt"], ""),
        refreshToken: readString(result, ["refreshToken"], refreshToken || ""),
        tenantId: readString(result, ["tenantId"], getStoredTenantId() ?? ""),
        tenantCode: readString(result, ["tenantCode"], getStoredTenantCode() ?? ""),
      });
      return result;
    },
    logout: async (tenantId) => {
      try { await client.post<unknown>(CMTS_API.auth.logout, {}, { query: { tenantId: resolveTenantId(tenantId), tenantCode: resolveTenantCode() } }); }
      finally { clearCmtsSession(); }
    },
    changePassword: (payload) => client.post<unknown>(CMTS_API.auth.changePassword, payload),
    requestOtp: (payload) => client.post<unknown>(CMTS_API.auth.requestOtp, payload),
    verifyOtp: (payload) => client.post<unknown>(CMTS_API.auth.verifyOtp, payload),
    sessions: async (tenantId) => mapCollection(await client.get<unknown>(CMTS_API.auth.sessions, { query: { tenantId: resolveTenantId(tenantId), tenantCode: resolveTenantCode() } }), (x) => x),
    revokeSession: (sessionId, tenantId) => client.post<unknown>(CMTS_API.auth.revokeSession(sessionId), {}, { query: { tenantId: resolveTenantId(tenantId), tenantCode: resolveTenantCode() } }),
    me: async (tenantId) => {
      const resolvedTenantId = resolveTenantId(tenantId);
      const me = await client.get<Record<string, unknown>>(CMTS_API.auth.me, { query: { tenantId: resolvedTenantId, tenantCode: resolveTenantCode() } });
      const roles = normalizeCmtsRoles(readStringArrayFrom(me.roles ?? me.Roles));
      const permissions = readStringArrayFrom(me.permissions ?? me.Permissions);
      const tenantFromResponse = readString(me, ["tenantId", "TenantId"], resolvedTenantId ?? "");
      const userId = readString(me, ["userId", "UserId", "id", "Id"], "");
      const orgScopes = readCollectionFrom(me.organizationScopes ?? me.OrganizationScopes, ["organizationScopes", "OrganizationScopes"]);
      const scopedOrgUnitIds = orgScopes.map((scope) => readString(readRecord(scope), ["organizationUnitId", "OrganizationUnitId", "orgUnitId", "OrgUnitId", "id", "Id"], "")).filter(Boolean);
      mergeCmtsSession({
        tenantId: tenantFromResponse,
        tenantCode: resolveTenantCode(),
        roles,
        userId,
        permissions,
        permissionCodes: permissions,
        organizationUnitId: readString(me, ["organizationUnitId", "OrganizationUnitId", "orgUnitId", "OrgUnitId"], scopedOrgUnitIds[0] ?? ""),
        organizationUnitIds: scopedOrgUnitIds,
        displayName: readString(me, ["displayName", "DisplayName", "fullName", "FullName", "name", "Name", "email", "Email"], "CMTS User"),
        email: readString(me, ["email", "Email"], ""),
      });
      return {
        userId,
        displayName: readString(me, ["displayName", "DisplayName", "fullName", "FullName", "name", "Name", "email", "Email"], "CMTS User"),
        email: readString(me, ["email", "Email"], ""),
        roles,
        permissions,
        tenantContext: { tenantId: tenantFromResponse, tenantName: readString(me, ["tenantName", "TenantName"], ""), timezoneCode: readString(me, ["timezoneCode", "TimezoneCode", "timeZone", "TimeZone"], "Asia/Kolkata"), liveMode: "live" },
        permissionContext: buildPermissionContext({ userId, tenantId: tenantFromResponse, roles, permissions }),
      };
    },
    myPermissions: async (tenantId) => readStringArrayFrom(await client.get<unknown>(CMTS_API.auth.myPermissions, { query: { tenantId: resolveTenantId(tenantId), tenantCode: resolveTenantCode() } }), ["permissions", "Permissions", "permissionCodes", "PermissionCodes"]),
    myOrganizationScope: async (tenantId) => readCollectionFrom(await client.get<unknown>(CMTS_API.auth.myOrganizationScope, { query: { tenantId: resolveTenantId(tenantId), tenantCode: resolveTenantCode() } }), ["scopes", "Scopes", "organizationScopes", "OrganizationScopes"]),
    myMenu: async (tenantId) => mapCollection(flattenMenu(await client.get<unknown>(CMTS_API.auth.myMenu, { query: { tenantId: resolveTenantId(tenantId), tenantCode: resolveTenantCode() } })), (m) => ({
      id: readString(m, ["id", "Id", "code", "Code", "menuCode", "MenuCode", "route", "Route"], ""),
      label: readString(m, ["label", "Label", "title", "Title", "name", "Name"], "CMTS"),
      href: readString(m, ["href", "Href", "route", "Route", "path", "Path", "routePath", "RoutePath"], ""),
      permission: readString(m, ["permission", "Permission", "permissionCode", "PermissionCode", "requiredPermission", "RequiredPermission"], ""),
      parentId: readString(m, ["parentId", "ParentId", "parentCode", "ParentCode"], "") || undefined,
      order: Number(readString(m, ["order", "Order", "sortOrder", "SortOrder"], "9999")),
    })).filter((item) => item.id && item.href),
  };
}

export function probeAuth(repository: AuthRepository) { return repositoryCall(() => repository.myPermissions(), "CMTS_AUTH_PROBE"); }
